import maplibregl from 'maplibre-gl';
import { env } from 'next-runtime-env';
import React, { useCallback } from 'react';

import Typography from '@/components/typography';
import { getGoogleTileSession } from '@/endpoints/google/getGoogleTileSession';
import { useEmbedContext } from '@/features/embed/embedProvider';
import { createGoogleStyle } from '@/features/embed/mapSettingsComponents/createGoogleStyle';
import {
  baseMapImgMap,
  MapStyle,
} from '@/features/mapbox/provider/useMapboxBaseMapState';
import MapOption from '@/features/navigation/bottom-navigation/toolkit/mapOption';
import { priorityLayers } from '@/utils/addLayer';

type Props = { map: maplibregl.Map | undefined };

export enum EmbedMapStyle {
  Google = 'google',
  Satellite = 'https://api.maptiler.com/maps/91a7c4fa-40bc-4aa8-af6c-e8368ca52351/style.json',
  Streets = 'https://api.maptiler.com/maps/acb9530e-53b4-488a-89f4-708b22a12bc3/style.json',
  Topography = 'https://api.maptiler.com/maps/b08b3220-fffb-45f8-9b80-f0dd932449ce/style.json',
  Light = 'https://api.maptiler.com/maps/dataviz-light/style.json',
  Dark = 'https://api.maptiler.com/maps/dataviz-dark/style.json',
}

export const embedBaseMapStyleMap: Record<EmbedMapStyle, string> = {
  [EmbedMapStyle.Google]: '', // This gets handled special
  [EmbedMapStyle.Satellite]:
    'https://api.maptiler.com/maps/91a7c4fa-40bc-4aa8-af6c-e8368ca52351/style.json',
  [EmbedMapStyle.Streets]:
    'https://api.maptiler.com/maps/acb9530e-53b4-488a-89f4-708b22a12bc3/style.json',
  [EmbedMapStyle.Topography]:
    'https://api.maptiler.com/maps/b08b3220-fffb-45f8-9b80-f0dd932449ce/style.json',
  [EmbedMapStyle.Light]:
    'https://api.maptiler.com/maps/dataviz-light/style.json',
  [EmbedMapStyle.Dark]: 'https://api.maptiler.com/maps/dataviz-dark/style.json',
};

/**
 * Return the URL to the map style or the map style object in the case of Google
 *  for the wanted base map style
 */
const getEmbedBaseMapStyle = async (selectedMap: EmbedMapStyle) => {
  if (selectedMap !== EmbedMapStyle.Google) {
    return embedBaseMapStyleMap[selectedMap];
  }

  const sessionData = await getGoogleTileSession();
  const gmaps_key = env('NEXT_PUBLIC_GOOGLE_MAPS_KEY');
  return createGoogleStyle(sessionData, gmaps_key);
};

export const selectedBaseMapKey = 'selected-base-map-v2';

export const EmbedBaseMap: React.FC<Props> = ({ map }) => {
  const { baseMap, setBaseMap, setBaseMapStyle } = useEmbedContext();

  const handleChangeStyle = useCallback(
    async (selectedMapStyle: EmbedMapStyle) => {
      if (!map) return;
      if (selectedMapStyle === baseMap) return;

      // Cleanup google tiles if present
      if (map.getLayer('gtiles')) {
        map.removeLayer('gtiles');
      }

      const style = await getEmbedBaseMapStyle(selectedMapStyle);
      setBaseMap(selectedMapStyle);
      setBaseMapStyle(style);

      priorityLayers.forEach((layer) => {
        if (map.getLayer(layer)) {
          map.moveLayer(layer);
        }
      });
    },
    [baseMap, map, setBaseMap, setBaseMapStyle]
  );

  return (
    <div className="flex flex-col gap-4 py-4 dark:bg-gray-100 dark:text-white">
      <Typography
        className="px-4  dark:bg-gray-100 dark:text-white"
        variant="button2"
      >
        Base Map
      </Typography>

      <div className="flex flex-row flex-wrap">
        <MapOption
          className="pl-4"
          imgLink={baseMapImgMap[MapStyle.SatelliteStreets]}
          label="Satellite"
          selected={baseMap === EmbedMapStyle.Satellite}
          onClick={() => {
            handleChangeStyle(EmbedMapStyle.Satellite);
          }}
        />

        <MapOption
          className="pl-6"
          imgLink="/img/base-map/google.png"
          label="Google"
          selected={baseMap === EmbedMapStyle.Google}
          onClick={() => {
            handleChangeStyle(EmbedMapStyle.Google);
          }}
        />

        <MapOption
          className="pl-4"
          imgLink={baseMapImgMap[MapStyle.Topography]}
          label="Topography"
          selected={baseMap === EmbedMapStyle.Topography}
          onClick={() => {
            handleChangeStyle(EmbedMapStyle.Topography);
          }}
        />

        <MapOption
          className="pl-4 pt-4"
          imgLink={baseMapImgMap[MapStyle.Streets]}
          label="Streets"
          selected={baseMap === EmbedMapStyle.Streets}
          onClick={() => {
            handleChangeStyle(EmbedMapStyle.Streets);
          }}
        />

        <MapOption
          className="pl-6 pt-4"
          imgLink={baseMapImgMap[MapStyle.Light]}
          label="Light"
          selected={baseMap === EmbedMapStyle.Light}
          onClick={() => {
            handleChangeStyle(EmbedMapStyle.Light);
          }}
        />

        <MapOption
          className="pl-6 pt-4"
          imgLink={baseMapImgMap[MapStyle.Dark]}
          label="Dark"
          selected={baseMap === EmbedMapStyle.Dark}
          onClick={() => {
            handleChangeStyle(EmbedMapStyle.Dark);
          }}
        />
      </div>
    </div>
  );
};

export default EmbedBaseMap;
